/**
 * ImportDialog — Upload Excel (.xlsx, .xls) → Xem trước / kiểm tra → Giá trị danh mục chưa có → Nhập → Báo cáo
 *
 * Chuẩn nhập danh mục (04/11/2026):
 * - Cột có `ref` khớp giá trị theo mã / tên với danh mục tham chiếu (refs). Giá trị chưa có thì hỏi từng giá trị:
 *   Tạo tự động (sửa lại tên trước khi tạo), Mở form chuẩn để xác nhận, hoặc Bỏ các dòng đó.
 * - onImport trả kết quả từng dòng → bước cuối báo Tạo mới / Cập nhật / Lỗi / Bỏ qua kèm lý do.
 * - Màn cũ còn dùng onConfirmImport (không báo cáo từng dòng) vẫn chạy như trước.
 */
import React, { useState, useCallback } from 'react';
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle2, Loader2, X, Plus, FileEdit, Ban } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
    parseExcelFile,
    validateExcelRows,
    type ExcelColumnDef as ColumnDef,
    type ParseExcelResult as ParseResult,
    type ExcelValidateResult as ValidateResult,
} from '@/lib/excel.utils';
import { validateExcelFile, extractErrorMessage } from '@/lib/utils';
import {
    buildRefIndex,
    normKey,
    ROW_KEY,
    summarize,
    type ImportRefs,
    type ImportResult,
    type ImportRowResult,
} from '@/lib/import-kit';
import { useEntityQuickAdd } from './useEntityQuickAdd';

interface ImportDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    columns: ColumnDef[];
    title: string;
    /** Cách cũ: nhận dòng hợp lệ, tự báo kết quả bằng thông báo */
    onConfirmImport?: (validRows: Record<string, unknown>[]) => Promise<void>;
    /** Cách chuẩn: mỗi dòng có khoá ROW_KEY (số dòng Excel); trả kết quả từng dòng cho báo cáo */
    onImport?: (rows: Record<string, unknown>[]) => Promise<ImportResult>;
    /** Danh mục tham chiếu cho các cột có `ref` */
    refs?: ImportRefs;
    onDownloadTemplate?: () => void;
}

type ImportStep = 'upload' | 'preview' | 'resolving' | 'resolve' | 'importing' | 'done';

type MissingAction = 'create' | 'form' | 'skip';

interface MissingValue {
    refKey: string;
    columnLabel: string;
    raw: string;
    rows: number[];
    action: MissingAction;
    /** Tên sẽ tạo (người dùng sửa được) */
    editName: string;
    /** Giá trị đã có sau khi tạo / xác nhận form */
    resolvedValue?: string;
}

/** Form chuẩn của danh mục (giống nút Thêm mới trong ô chọn), điền sẵn giá trị lấy từ file */
function RefQuickAddForm({
    type,
    defaults,
    onDone,
    onCancel,
}: {
    type: string;
    defaults: Record<string, unknown>;
    onDone: (id: string, label: string) => void;
    onCancel: () => void;
}) {
    const qa = useEntityQuickAdd(type, {});
    const content = qa.addFormContent;
    if (!React.isValidElement(content)) return null;
    return (
        <Dialog open onOpenChange={(o) => !o && onCancel()}>
            <DialogContent
                className="max-w-[95vw] w-full max-h-[95vh] overflow-y-auto sm:max-w-4xl"
                onPointerDownOutside={(e) => e.preventDefault()}
                onInteractOutside={(e) => e.preventDefault()}
            >
                <DialogHeader className="sr-only">
                    <DialogTitle>Xác nhận thêm mới</DialogTitle>
                    <DialogDescription>Biểu mẫu tạo mới dữ liệu từ file nhập</DialogDescription>
                </DialogHeader>
                {React.cloneElement(content as React.ReactElement<any>, {
                    onSuccess: (item: { id: string; label: string }) => {
                        qa.onCreated(item.id);
                        onDone(item.id, item.label);
                    },
                    onCancel,
                    initialData: { ...((content.props as any)?.initialData || {}), ...defaults },
                })}
            </DialogContent>
        </Dialog>
    );
}

export function ImportDialog({
    open,
    onOpenChange,
    columns,
    title,
    onConfirmImport,
    onImport,
    refs,
    onDownloadTemplate,
}: ImportDialogProps) {
    const [step, setStep] = useState<ImportStep>('upload');
    const [parseResult, setParseResult] = useState<ParseResult | null>(null);
    const [validateResult, setValidateResult] = useState<ValidateResult | null>(null);
    const [fileName, setFileName] = useState('');
    const [error, setError] = useState('');
    const [dragOver, setDragOver] = useState(false);
    const [missing, setMissing] = useState<MissingValue[]>([]);
    const [pendingRows, setPendingRows] = useState<Record<string, unknown>[]>([]);
    const [formFor, setFormFor] = useState<number | null>(null);
    const [report, setReport] = useState<ImportResult | null>(null);

    const reset = useCallback(() => {
        setStep('upload');
        setParseResult(null);
        setValidateResult(null);
        setFileName('');
        setError('');
        setDragOver(false);
        setMissing([]);
        setPendingRows([]);
        setFormFor(null);
        setReport(null);
    }, []);

    const handleClose = (open: boolean) => {
        if (!open) reset();
        onOpenChange(open);
    };

    const processFile = async (file: File) => {
        setError('');
        setFileName(file.name);

        const validation = validateExcelFile(file);
        if (!validation.valid) {
            setError(validation.error);
            return;
        }

        try {
            const parsed = await parseExcelFile(file);
            if (parsed.rows.length === 0) {
                setError('File không có dữ liệu hoặc sai định dạng.');
                return;
            }

            setParseResult(parsed);

            // Validate
            const validated = validateExcelRows(parsed.rows, columns);
            setValidateResult(validated);
            setStep('preview');
        } catch (err) {
            setError(`Lỗi đọc file: ${extractErrorMessage(err)}`);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) processFile(file);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) processFile(file);
    };

    /** Lỗi kiểm tra trên trình duyệt (thiếu ô bắt buộc, sai số…) → dòng lỗi trong báo cáo */
    const validationFailures = (): ImportRowResult[] => {
        const byRow = new Map<number, string[]>();
        for (const e of validateResult?.errors || []) {
            byRow.set(e.row, [...(byRow.get(e.row) || []), `${e.column}: ${e.message}`]);
        }
        const first = columns[0];
        const codeAt = (row: number) => {
            const r = parseResult?.rows[row - 2];
            const v = r ? r[first?.label] ?? r[first?.key] : '';
            return v ? String(v) : undefined;
        };
        return [...byRow.entries()].map(([row, msgs]) => ({ row, code: codeAt(row), status: 'failed' as const, message: msgs.join('; ') }));
    };

    const runImport = async (rows: Record<string, unknown>[], skipped: ImportRowResult[], createdRefs: { label: string; value: string }[]) => {
        setStep('importing');
        try {
            const result: ImportResult = rows.length > 0 && onImport ? await onImport(rows) : { rows: [] };
            const all = [...result.rows, ...skipped, ...validationFailures()].sort((a, b) => a.row - b.row);
            setReport({ rows: all, createdRefs: [...createdRefs, ...(result.createdRefs || [])] });
            setStep('done');
        } catch (err) {
            setError(`Lỗi import: ${extractErrorMessage(err)}`);
            setStep('preview');
        }
    };

    const handleImport = async () => {
        if (!validateResult || validateResult.validRows.length === 0) return;
        setError('');
        if (!onImport) {
            // Cách cũ
            setStep('importing');
            try {
                await onConfirmImport?.(validateResult.validRows);
                setStep('done');
            } catch (err) {
                setError(`Lỗi import: ${extractErrorMessage(err)}`);
                setStep('preview');
            }
            return;
        }

        const rowNos = validateResult.validRowNumbers || validateResult.validRows.map((_, i) => i + 2);
        const rows: Record<string, unknown>[] = validateResult.validRows.map((r, i) => ({ ...r, [ROW_KEY]: rowNos[i] }));
        const refCols = columns.filter((c) => c.ref && refs?.[c.ref] && c.importable !== false);
        if (refCols.length === 0) {
            await runImport(rows, [], []);
            return;
        }

        // Khớp giá trị cột tham chiếu với danh mục
        setStep('resolving');
        try {
            const usedRefs = [...new Set(refCols.map((c) => c.ref as string))];
            const indexes = new Map<string, Map<string, string>>();
            await Promise.all(usedRefs.map(async (k) => indexes.set(k, buildRefIndex(await refs![k].load()))));
            const miss = new Map<string, MissingValue>();
            for (const row of rows) {
                for (const col of refCols) {
                    const raw = row[col.key];
                    if (raw === null || raw === undefined || String(raw).trim() === '') continue;
                    const found = indexes.get(col.ref!)!.get(normKey(raw));
                    if (found !== undefined) {
                        row[col.key] = found;
                        continue;
                    }
                    const id = `${col.ref}|${normKey(raw)}`;
                    const def = refs![col.ref!];
                    const cur = miss.get(id);
                    if (cur) cur.rows.push(Number(row[ROW_KEY]));
                    else
                        miss.set(id, {
                            refKey: col.ref!,
                            columnLabel: col.label,
                            raw: String(raw).trim(),
                            rows: [Number(row[ROW_KEY])],
                            action: def.canCreate && def.create ? 'create' : def.canCreate && def.quickAddType ? 'form' : 'skip',
                            editName: String(raw).trim(),
                        });
                }
            }
            if (miss.size === 0) {
                await runImport(rows, [], []);
                return;
            }
            setPendingRows(rows);
            setMissing([...miss.values()]);
            setStep('resolve');
        } catch (err) {
            setError(`Lỗi đọc danh mục: ${extractErrorMessage(err)}`);
            setStep('preview');
        }
    };

    const updateMissing = (idx: number, patch: Partial<MissingValue>) =>
        setMissing((list) => list.map((m, i) => (i === idx ? { ...m, ...patch } : m)));

    const formPending = missing.some((m) => m.action === 'form' && !m.resolvedValue);

    /** Tạo các giá trị chọn "Tạo tự động", áp giá trị vào dòng, dòng có giá trị bị bỏ → báo cáo Bỏ qua */
    const handleResolveContinue = async () => {
        setStep('importing');
        const createdRefs: { label: string; value: string }[] = [];
        const resolved = [...missing];
        const failedCreate: ImportRowResult[] = [];
        for (const m of resolved) {
            if (m.action === 'create' && !m.resolvedValue) {
                const def = refs![m.refKey];
                try {
                    m.resolvedValue = await def.create!(m.editName.trim() || m.raw);
                    createdRefs.push({ label: def.label, value: m.editName.trim() || m.raw });
                } catch (err) {
                    for (const r of m.rows)
                        failedCreate.push({ row: r, status: 'failed', message: `Không tạo được ${def.label} "${m.editName}": ${extractErrorMessage(err)}` });
                    m.action = 'skip';
                    m.resolvedValue = undefined;
                    (m as any).createFailed = true;
                }
            } else if (m.action === 'form' && m.resolvedValue) {
                createdRefs.push({ label: refs![m.refKey].label, value: m.editName || m.raw });
            }
        }
        const lookup = new Map(resolved.map((m) => [`${m.refKey}|${normKey(m.raw)}`, m]));
        const refCols = columns.filter((c) => c.ref && refs?.[c.ref]);
        const keep: Record<string, unknown>[] = [];
        const skipped: ImportRowResult[] = [...failedCreate];
        for (const row of pendingRows) {
            let drop: MissingValue | null = null;
            for (const col of refCols) {
                const m = lookup.get(`${col.ref}|${normKey(row[col.key])}`);
                if (!m) continue;
                if (m.resolvedValue !== undefined) row[col.key] = m.resolvedValue;
                else drop = m;
            }
            if (!drop) keep.push(row);
            else if (!(drop as any).createFailed)
                skipped.push({
                    row: Number(row[ROW_KEY]),
                    code: row[columns[0]?.key] ? String(row[columns[0].key]) : undefined,
                    status: 'skipped',
                    message: `${refs![drop.refKey].label} "${drop.raw}" chưa có trong danh mục`,
                });
        }
        await runImport(keep, skipped, createdRefs);
    };

    const stats = report ? summarize(report) : null;
    const problemRows = report ? report.rows.filter((r) => r.status === 'failed' || r.status === 'skipped') : [];
    const formItem = formFor !== null ? missing[formFor] : null;

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-3xl sm:max-w-3xl max-h-[85vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
                        {/^(import|nhập)\b/i.test(title.trim()) ? title : `Import ${title}`}
                    </DialogTitle>
                </DialogHeader>

                {/* Step: Upload */}
                {step === 'upload' && (
                    <div className="flex-1 flex flex-col gap-3">
                        <div
                            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${dragOver
                                    ? 'border-emerald-400 bg-emerald-50'
                                    : 'border-slate-200 hover:border-slate-300'
                                }`}
                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={handleDrop}
                            onClick={() => document.getElementById('import-file-input')?.click()}
                        >
                            <Upload className="h-10 w-10 text-slate-400 mx-auto mb-3" />
                            <p className="text-sm text-slate-600 font-medium">
                                Kéo thả file Excel vào đây hoặc <span className="text-indigo-600 underline">chọn file</span>
                            </p>
                            <p className="text-xs text-slate-400 mt-1">
                                Chỉ hỗ trợ: .xlsx, .xls (Excel)
                            </p>
                            <input
                                id="import-file-input"
                                type="file"
                                accept=".xlsx,.xls"
                                className="hidden"
                                onChange={handleFileChange}
                            />
                        </div>

                        {error && (
                            <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}

                        <div className="text-xs text-slate-500 space-y-2">
                            <p><strong>Lưu ý:</strong></p>
                            <ul className="list-disc ml-4 space-y-0.5">
                                <li>Dòng đầu tiên là header (tên cột)</li>
                                <li>Cột có tiêu đề màu cam là bắt buộc (dùng file mẫu hoặc file vừa xuất)</li>
                                <li>Cột chỉ để xem (VD tồn kho) được bỏ qua khi nhập</li>
                                {onImport && <li>Mã đã có thì cập nhật, mã mới thì tạo; giá trị danh mục chưa có sẽ được hỏi trước khi thêm</li>}
                            </ul>
                            {onDownloadTemplate && (
                                <div className="mt-4 flex justify-center">
                                    <Button variant="outline" size="sm" onClick={onDownloadTemplate} className="gap-2">
                                        <FileSpreadsheet className="h-4 w-4 text-emerald-600" /> Tải form mẫu Excel
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Step: Preview */}
                {step === 'preview' && parseResult && validateResult && (
                    <div className="flex-1 flex flex-col gap-3 overflow-hidden">
                        {/* Stats */}
                        <div className="flex items-center gap-3 flex-wrap">
                            <Badge variant="secondary" className="text-xs">
                                📄 {fileName}
                            </Badge>
                            <Badge variant="secondary" className="text-xs">
                                Tổng: {parseResult.totalRows} dòng
                            </Badge>
                            <Badge className="bg-emerald-100 text-emerald-800 text-xs">
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Hợp lệ: {validateResult.validRows.length}
                            </Badge>
                            {validateResult.errors.length > 0 && (
                                <Badge className="bg-red-100 text-red-800 text-xs">
                                    <AlertCircle className="h-3 w-3 mr-1" />
                                    Lỗi: {validateResult.errors.length}
                                </Badge>
                            )}
                        </div>

                        {/* Preview Table */}
                        <ScrollArea className="flex-1 max-h-[300px] border rounded-lg">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-10 text-xs">#</TableHead>
                                        {columns.slice(0, 6).map(col => (
                                            <TableHead key={col.key} className="text-xs whitespace-nowrap">
                                                {col.label}
                                                {col.required && <span className="text-red-500 ml-0.5">*</span>}
                                            </TableHead>
                                        ))}
                                        {columns.length > 6 && (
                                            <TableHead className="text-xs text-slate-400">+{columns.length - 6} cột</TableHead>
                                        )}
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {parseResult.rows.slice(0, 5).map((row, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell className="text-xs text-slate-400">{idx + 1}</TableCell>
                                            {columns.slice(0, 6).map(col => (
                                                <TableCell key={col.key} className="text-xs max-w-[150px] break-words whitespace-normal">
                                                    {row[col.label] || row[col.key] || '—'}
                                                </TableCell>
                                            ))}
                                            {columns.length > 6 && <TableCell className="text-xs text-slate-400">…</TableCell>}
                                        </TableRow>
                                    ))}
                                    {parseResult.totalRows > 5 && (
                                        <TableRow>
                                            <TableCell colSpan={Math.min(columns.length, 6) + 2} className="text-center text-xs text-slate-400 py-2">
                                                ... và {parseResult.totalRows - 5} dòng nữa
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </ScrollArea>

                        {/* Errors */}
                        {validateResult.errors.length > 0 && (
                            <ScrollArea className="max-h-[120px] border border-red-200 rounded-lg bg-red-50 p-2">
                                <div className="space-y-1">
                                    {validateResult.errors.slice(0, 10).map((err, i) => (
                                        <div key={i} className="text-xs text-red-700 flex gap-1">
                                            <span className="font-mono text-red-500 shrink-0">Dòng {err.row}:</span>
                                            <span>{err.column} — {err.message}</span>
                                        </div>
                                    ))}
                                    {validateResult.errors.length > 10 && (
                                        <div className="text-xs text-red-500 font-medium">
                                            ... và {validateResult.errors.length - 10} lỗi nữa
                                        </div>
                                    )}
                                </div>
                            </ScrollArea>
                        )}

                        {error && (
                            <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Step: Giá trị danh mục chưa có */}
                {step === 'resolve' && (
                    <div className="flex-1 flex flex-col gap-3 overflow-hidden" data-testid="import-missing-refs">
                        <div className="text-sm text-slate-700">
                            File có <strong>{missing.length}</strong> giá trị chưa có trong danh mục. Chọn cách xử lý cho từng giá trị:
                            <span className="text-slate-500"> Tạo tự động (sửa lại tên nếu cần), Mở form để kiểm tra đủ thông tin, hoặc Bỏ các dòng đó.</span>
                        </div>
                        <ScrollArea className="flex-1 max-h-[360px] border rounded-lg">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="text-xs">Cột</TableHead>
                                        <TableHead className="text-xs">Giá trị trong file</TableHead>
                                        <TableHead className="text-xs">Dòng</TableHead>
                                        <TableHead className="text-xs">Xử lý</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {missing.map((m, idx) => {
                                        const def = refs![m.refKey];
                                        const canAuto = !!(def.canCreate && def.create);
                                        const canForm = !!(def.canCreate && def.quickAddType);
                                        return (
                                            <TableRow key={`${m.refKey}-${m.raw}`} data-testid="import-missing-row">
                                                <TableCell className="text-xs align-top">{m.columnLabel}</TableCell>
                                                <TableCell className="text-xs align-top">
                                                    {m.action === 'create' ? (
                                                        <Input
                                                            aria-label={`Tên sẽ tạo cho ${m.raw}`}
                                                            className="h-8 text-xs"
                                                            value={m.editName}
                                                            onChange={(e) => updateMissing(idx, { editName: e.target.value })}
                                                        />
                                                    ) : (
                                                        <span className="font-medium">{m.raw}</span>
                                                    )}
                                                    {m.resolvedValue && m.action === 'form' && (
                                                        <div className="text-[11px] text-emerald-700 mt-0.5">Đã tạo qua form</div>
                                                    )}
                                                    {!def.canCreate && (
                                                        <div className="text-[11px] text-amber-700 mt-0.5">
                                                            {def.createHint || `Chưa có quyền thêm ${def.label}`}
                                                        </div>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-xs align-top text-slate-500 whitespace-normal max-w-[120px]">
                                                    {m.rows.slice(0, 6).join(', ')}{m.rows.length > 6 ? ` … (${m.rows.length})` : ''}
                                                </TableCell>
                                                <TableCell className="text-xs align-top">
                                                    <div className="flex flex-wrap gap-1">
                                                        {canAuto && (
                                                            <Button
                                                                type="button"
                                                                size="sm"
                                                                variant={m.action === 'create' ? 'default' : 'outline'}
                                                                className="h-7 text-xs gap-1"
                                                                onClick={() => updateMissing(idx, { action: 'create', resolvedValue: undefined })}
                                                            >
                                                                <Plus className="h-3 w-3" /> Tạo tự động
                                                            </Button>
                                                        )}
                                                        {canForm && (
                                                            <Button
                                                                type="button"
                                                                size="sm"
                                                                variant={m.action === 'form' ? 'default' : 'outline'}
                                                                className="h-7 text-xs gap-1"
                                                                onClick={() => {
                                                                    updateMissing(idx, { action: 'form' });
                                                                    if (!m.resolvedValue || m.action !== 'form') setFormFor(idx);
                                                                }}
                                                            >
                                                                <FileEdit className="h-3 w-3" /> Mở form
                                                            </Button>
                                                        )}
                                                        <Button
                                                            type="button"
                                                            size="sm"
                                                            variant={m.action === 'skip' ? 'default' : 'outline'}
                                                            className="h-7 text-xs gap-1"
                                                            onClick={() => updateMissing(idx, { action: 'skip', resolvedValue: undefined })}
                                                        >
                                                            <Ban className="h-3 w-3" /> Bỏ dòng
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </ScrollArea>
                        {formPending && (
                            <div className="text-xs text-amber-700">Còn giá trị chọn Mở form nhưng chưa lưu form — lưu form hoặc chọn cách khác.</div>
                        )}
                    </div>
                )}

                {/* Step: Importing */}
                {(step === 'importing' || step === 'resolving') && (
                    <div className="flex-1 flex flex-col items-center justify-center gap-3 py-8">
                        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                        <p className="text-sm text-slate-600">
                            {step === 'resolving' ? 'Đang đối chiếu danh mục...' : `Đang import ${validateResult?.validRows.length} dòng...`}
                        </p>
                    </div>
                )}

                {/* Step: Done */}
                {step === 'done' && !report && (
                    <div className="flex-1 flex flex-col items-center justify-center gap-3 py-8">
                        <CheckCircle2 className="h-10 w-10 text-emerald-500" />
                        <p className="text-sm font-medium text-slate-900">Import thành công!</p>
                        <p className="text-xs text-slate-500">
                            Đã import {validateResult?.validRows.length} dòng dữ liệu.
                        </p>
                    </div>
                )}

                {step === 'done' && report && stats && (
                    <div className="flex-1 flex flex-col gap-3 overflow-hidden" data-testid="import-report">
                        <div className="flex items-center gap-2 flex-wrap">
                            {stats.failed + stats.skipped === 0 ? (
                                <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                            ) : (
                                <AlertCircle className="h-5 w-5 text-amber-500" />
                            )}
                            <span className="text-sm font-medium text-slate-900">Kết quả nhập {fileName}</span>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap text-xs">
                            <Badge className="bg-emerald-100 text-emerald-800" data-testid="import-report-created">Tạo mới: {stats.created}</Badge>
                            <Badge className="bg-sky-100 text-sky-800" data-testid="import-report-updated">Cập nhật: {stats.updated}</Badge>
                            <Badge className="bg-red-100 text-red-800" data-testid="import-report-failed">Lỗi: {stats.failed}</Badge>
                            {stats.skipped > 0 && (
                                <Badge className="bg-amber-100 text-amber-800" data-testid="import-report-skipped">Bỏ qua: {stats.skipped}</Badge>
                            )}
                        </div>
                        {(report.createdRefs?.length || 0) > 0 && (
                            <div className="text-xs text-slate-600" data-testid="import-report-refs">
                                <span className="font-medium">Đã thêm vào danh mục:</span>{' '}
                                {report.createdRefs!.map((r) => `${r.label} "${r.value}"`).join(', ')}
                            </div>
                        )}
                        {problemRows.length > 0 && (
                            <ScrollArea className="flex-1 max-h-[300px] border rounded-lg">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="text-xs w-16">Dòng</TableHead>
                                            <TableHead className="text-xs">Mã</TableHead>
                                            <TableHead className="text-xs">Kết quả</TableHead>
                                            <TableHead className="text-xs">Lý do</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {problemRows.map((r, i) => (
                                            <TableRow key={i} data-testid="import-report-problem">
                                                <TableCell className="text-xs font-mono">{r.row}</TableCell>
                                                <TableCell className="text-xs">{r.code || '—'}</TableCell>
                                                <TableCell className="text-xs">{r.status === 'failed' ? 'Lỗi' : 'Bỏ qua'}</TableCell>
                                                <TableCell className="text-xs whitespace-normal break-words">{r.message}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </ScrollArea>
                        )}
                    </div>
                )}

                {/* Footer */}
                <DialogFooter className="gap-2">
                    {step === 'preview' && (
                        <>
                            <Button variant="ghost" size="sm" onClick={reset} className="gap-1">
                                <X className="h-3.5 w-3.5" /> Chọn lại
                            </Button>
                            <Button
                                size="sm"
                                onClick={handleImport}
                                disabled={!validateResult || validateResult.validRows.length === 0}
                                className="gap-1 bg-emerald-600 hover:bg-emerald-700"
                            >
                                <Upload className="h-3.5 w-3.5" />
                                Import {validateResult?.validRows.length} dòng hợp lệ
                            </Button>
                        </>
                    )}
                    {step === 'resolve' && (
                        <>
                            <Button variant="ghost" size="sm" onClick={() => setStep('preview')} className="gap-1">
                                <X className="h-3.5 w-3.5" /> Quay lại
                            </Button>
                            <Button
                                size="sm"
                                onClick={handleResolveContinue}
                                disabled={formPending}
                                className="gap-1 bg-emerald-600 hover:bg-emerald-700"
                                data-testid="import-missing-continue"
                            >
                                <Upload className="h-3.5 w-3.5" />
                                Xác nhận &amp; nhập
                            </Button>
                        </>
                    )}
                    {step === 'done' && (
                        <Button size="sm" onClick={() => handleClose(false)}>
                            Đóng
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>

            {formItem && refs?.[formItem.refKey]?.quickAddType && (
                <RefQuickAddForm
                    type={refs[formItem.refKey].quickAddType!}
                    defaults={refs[formItem.refKey].formDefaults?.(formItem.raw) || { name: formItem.raw }}
                    onDone={(id) => {
                        updateMissing(formFor!, { resolvedValue: id, action: 'form' });
                        setFormFor(null);
                    }}
                    onCancel={() => setFormFor(null)}
                />
            )}
        </Dialog>
    );
}
